import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  ALL_PERMISSIONS,
  channelPermissions,
  combineRoles,
  computePermissions,
  hasPermission,
  isTimedOut,
  memberRank,
  Permissions,
  rulesPending,
  safetyHold,
  withTimeout,
  type PermissionName,
  type SafetyHold,
  type RoleDTO,
} from '@clanfire/shared';
import { useAuth } from '@/stores/auth';
import { useServers } from '@/stores/servers';
import { useThreads } from '@/stores/threads';

export interface MyPermissions {
  isOwner: boolean;
  rank: number;
  permissions: number;
  can: (permission: PermissionName) => boolean;
  /** When your timeout here ends, while you're timed out. */
  timedOutUntil: string | null;
  /** You haven't accepted the server's rules yet (you can read, but not chat). */
  rulesPending: boolean;
  /** The server's safety level still holds you back (a new account, or an unconfirmed email): you can read, but not chat. */
  safetyHold: SafetyHold | null;
}

const NONE: MyPermissions = { isOwner: false, rank: -1, permissions: 0, can: () => false, timedOutUntil: null, rulesPending: false, safetyHold: null };

/** Re-renders every half minute until `until` (so a countdown stays right), and once more when it's reached. */
function useRerenderUntil(until: number | null | undefined) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!until || until <= Date.now()) return;
    const tick = () => setTick((t) => t + 1);
    const interval = window.setInterval(tick, 30_000);
    const end = window.setTimeout(tick, Math.min(until - Date.now() + 250, 2 ** 31 - 1));
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(end);
    };
  }, [until]);
}

/**
 * When a timeout ends, while it's still running. Re-renders when it runs out,
 * so whatever it greyed out comes back by itself.
 */
export function useActiveTimeout(timeoutUntil: string | null | undefined): string | null {
  const [, setTick] = useState(0);
  const active = isTimedOut(timeoutUntil);
  useEffect(() => {
    if (!active || !timeoutUntil) return;
    // setTimeout can't wait longer than ~24.8 days; timeouts are at most a week.
    const wait = Math.min(new Date(timeoutUntil).getTime() - Date.now() + 250, 2 ** 31 - 1);
    const timer = window.setTimeout(() => setTick((t) => t + 1), wait);
    return () => window.clearTimeout(timer);
  }, [active, timeoutUntil]);
  return active ? timeoutUntil! : null;
}

/** The server's default role plus the given roles. */
function withDefaultRole(roles: Record<string, RoleDTO>, serverId: string, roleIds: readonly string[] | undefined): RoleDTO[] {
  return Object.values(roles).filter((r) => r.serverId === serverId && (r.isDefault || roleIds?.includes(r.id)));
}

/**
 * The current user's effective permissions in a server. Used only to decide
 * what UI to show — every action is re-validated by the API.
 */
export function useMyPermissions(serverId: string | undefined): MyPermissions {
  const myId = useAuth((s) => s.user?.id);
  const server = useServers((s) => (serverId ? s.servers[serverId] : undefined));
  const roles = useServers(useShallow((s) => (server ? withDefaultRole(s.roles, server.id, server.me.roleIds) : [])));
  const timedOutUntil = useActiveTimeout(server?.me.timeoutUntil);
  const me = useAuth((s) => s.user);
  const emailOff = useAuth((s) => s.config.emailVerification === 'off');
  const allRoles = useServers((s) => s.roles);
  // New members may have to wait (or confirm their email) first; anyone with a role is trusted.
  const held =
    server && me
      ? safetyHold(server.safetyLevel ?? 'NONE', {
          emailVerified: me.emailVerified || emailOff,
          accountCreatedAt: me.createdAt,
          joinedAt: server.me.joinedAt,
          // Only a role someone gave you counts; one you picked yourself doesn't.
          hasRole: server.me.roleIds.some((id) => allRoles[id] && !allRoles[id].selfAssignable),
        })
      : null;
  useRerenderUntil(held?.until);
  if (!server || !myId) return NONE;
  const isOwner = server.ownerId === myId;
  const combined = combineRoles(roles);
  const base = computePermissions({ isOwner, rolePermissions: combined.permissions });
  // The owner and Administrators can't be timed out, and never wait on the rules.
  const pending = base !== ALL_PERMISSIONS && rulesPending(server, server.me.rulesAcceptedAt);
  const hold = base === ALL_PERMISSIONS ? null : held;
  const permissions = withTimeout(base, !!timedOutUntil || pending || !!hold);
  return {
    isOwner,
    rank: memberRank({ isOwner, rolePosition: combined.position }),
    permissions,
    can: (permission) => hasPermission(permissions, Permissions[permission]),
    timedOutUntil: base === ALL_PERMISSIONS ? null : timedOutUntil,
    rulesPending: pending,
    safetyHold: hold,
  };
}

/**
 * The current user's permissions in one channel: their server permissions with
 * the channel's overrides applied. For deciding what to show in that channel.
 * A thread's are its parent channel's.
 */
export function useChannelPermissions(channelId: string | null | undefined): MyPermissions {
  const parentId = useThreads((s) => (channelId ? s.threads[channelId]?.parentId : undefined));
  const channel = useServers((s) => (channelId ? s.channels[parentId ?? channelId] : undefined));
  const server = useMyPermissions(channel?.serverId);
  const me = useServers((s) => (channel ? s.servers[channel.serverId]?.me : undefined));
  const roles = useServers(useShallow((s) => (channel && me ? withDefaultRole(s.roles, channel.serverId, me.roleIds) : [])));
  if (!channel || !me || server === NONE) return NONE;
  const permissions = withTimeout(
    channelPermissions({
      isOwner: server.isOwner,
      rolePermissions: combineRoles(roles).permissions,
      defaultRoleId: roles.find((r) => r.isDefault)?.id ?? '',
      roleIds: me.roleIds,
      overwrites: channel.overwrites,
    }),
    !!server.timedOutUntil || server.rulesPending || !!server.safetyHold,
  );
  return { ...server, permissions, can: (permission) => hasPermission(permissions, Permissions[permission]) };
}

/** Rank of an arbitrary member (their highest role), for hierarchy-aware UI (kick/ban/role buttons). */
export function useMemberRank(serverId: string | undefined, userId: string | undefined): { rank: number; isOwner: boolean } {
  const server = useServers((s) => (serverId ? s.servers[serverId] : undefined));
  const position = useServers((s) => {
    if (!serverId || !userId) return 0;
    return combineRoles(withDefaultRole(s.roles, serverId, s.members[serverId]?.[userId]?.roleIds)).position;
  });
  const isOwner = !!server && server.ownerId === userId;
  return { rank: memberRank({ isOwner, rolePosition: position }), isOwner };
}
